import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { AppDispatch } from '../store/store';
import type { AsyncThunk, AsyncThunkAction } from '@reduxjs/toolkit';

/**
 * Custom hook for toast notifications with i18n support
 * Provides consistent toast patterns across the application
 */
export const useToast = () => {
  const { t } = useTranslation();

  type ToastOptions<T> = {
    loading: string;
    success: string | ((result: T) => string);
    error?: string | ((error: unknown) => string);
    onSuccess?: (result: T) => void;
    onError?: (error: unknown) => void;
    onFinally?: () => void;
  };

  /**
   * Show a toast for a Redux async thunk action
   * Automatically handles loading, success, and error states
   */
  const toastPromise = <T>(
    promise: Promise<T>,
    options: ToastOptions<T>
  ): Promise<T> => {
    return toast
      .promise(promise, {
        loading: options.loading,
        success: (result: T) => {
          const message =
            typeof options.success === 'function'
              ? options.success(result)
              : options.success;
          options.onSuccess?.(result);
          return message;
        },
        error: (error: unknown) => {
          const message = options.error
            ? typeof options.error === 'function'
              ? options.error(error)
              : options.error
            : t('error') || 'An error occurred';
          console.error('Toast promise error:', error);
          options.onError?.(error);
          return message;
        },
      })
      .finally(() => {
        options.onFinally?.();
      }) as Promise<T>;
  };

  /**
   * Execute a Redux thunk with toast notifications
   * Type-safe wrapper for Redux async thunks
   */
  function executeWithToast<Returned>(
    dispatch: AppDispatch,
    thunk: AsyncThunk<Returned, void, {}>,
    options: ToastOptions<Returned>
  ): Promise<Returned>;
  function executeWithToast<Returned, ThunkArg>(
    dispatch: AppDispatch,
    thunk: AsyncThunk<Returned, ThunkArg, {}>,
    payload: ThunkArg,
    options: ToastOptions<Returned>
  ): Promise<Returned>;
  function executeWithToast<Returned, ThunkArg>(
    dispatch: AppDispatch,
    thunk: AsyncThunk<Returned, ThunkArg, {}>,
    payloadOrOptions: ThunkArg | ToastOptions<Returned>,
    maybeOptions?: ToastOptions<Returned>
  ): Promise<Returned> {
    type ThunkArgType = Parameters<typeof thunk>[0];
    const hasPayload = maybeOptions !== undefined;
    const payload = hasPayload ? (payloadOrOptions as ThunkArgType) : undefined;
    const options = hasPayload ? maybeOptions : (payloadOrOptions as ToastOptions<Returned>);
    const thunkAction = (thunk as unknown as (arg: unknown) => AsyncThunkAction<Returned, ThunkArg, {}>)(payload);
    return toastPromise(dispatch(thunkAction).unwrap(), options);
  }

  /**
   * Show success toast
   */
  const showSuccess = (message: string) => {
    toast.success(message);
  };

  /**
   * Show error toast
   */
  const showError = (message: string) => {
    toast.error(message);
  };

  /**
   * Show loading toast
   */
  const showLoading = (message: string) => {
    return toast.loading(message);
  };

  return {
    toastPromise,
    executeWithToast,
    showSuccess,
    showError,
    showLoading,
  };
};
